import type { AxiosResponse } from 'axios';
import { Installments } from 'components/Cart/OrderSummary/types';
import { Environment } from 'utils/Environment';

export const INSTALLMENT_LENGTHS = {
  [Installments.P]: 1,
  [Installments.SL36]: 36,
  [Installments.SL48]: 48,
};

export interface IInstallmentPlan {
  code: Installments;
  name: string;
}

interface IProductPriceResellerOverride {
  reseller: IReseller;
  price: IMoney;
}

interface ISoftwarePlanPricing {
  monthlyPrice: IMoney;
  baseMonthlyPrice: IMoney;
  additionalDevicePrice: IMoney;
  transactionRates: ISoftwarePlanTransactionRates;
}

interface ISoftwarePlanTransactionRates {
  keyedInRate: ITransactionRate;
  inPersonRate: ITransactionRate;
  resellerOverrides: ITransactionRateResellerOverride[];
}

interface ITransactionRateResellerOverride {
  reseller: IReseller;
  transactionRates: ISoftwarePlanTransactionRates;
}

export interface ITransactionRate {
  percentage: number;
  fixedAmount: IMoney;
}

export interface IResellerChannel {
  chainAgentId: string;
}

export interface IReseller {
  uuid: string;
  cosUuid: string;
  name: string;
  referralId: string;
  isMsrpEnabled: boolean;
  isDemoAvailable: boolean;
  supportedInstallmentPlans: IInstallmentPlan[];
  resellerChannel?: IResellerChannel;
}

export interface IMoney {
  value: number;
  currencyCode: string;
}

export interface IProductPricing {
  installmentPlan: IInstallmentPlan;
  price: IMoney;
  priceOverrides: IProductPriceResellerOverride[];
}

export interface IProduct {
  id: string;
  name: string;
  fdmpId: string;
  msrp: IMoney;
  isIncludedInMultiDevicePricing: boolean;
  isSelfServicePurchaseEnabled: boolean;
  pricing: IProductPricing[];
  supportedSoftwarePlans?: ISoftwarePlan[];
  accessories?: IProduct[];
}

export interface IRecommendedAddonProduct {
  product: IProduct;
}

export interface IIncludedProduct {
  product: IProduct;
  quantity: number;
}

export interface ISoftwarePlan {
  id: string;
  cosUuid: string;
  name: string;
  pricing: ISoftwarePlanPricing | null;
}

export interface IBundle {
  id: string;
  name: string;
  softwarePlan: ISoftwarePlan;
  isSelfServicePurchaseEnabled: boolean;
  includedProducts: IIncludedProduct[];
  recommendedAddons: IRecommendedAddonProduct[];
  productPricing: IProductPricing[];
  softwarePlanPricing: ISoftwarePlanPricing;
  msrp: IMoney | null;
}

export type InstallmentPlanPricing = Partial<Record<Exclude<Installments, Installments.P>, IMoney>>;

export type SubscriptionPlanPricing = Partial<Record<Exclude<Installments, Installments.P>, IMoney>>;

export const fetchBundlesDetail = async ({
  resellerId,
  bundleIds,
}: {
  resellerId: string;
  bundleIds: string[];
}): Promise<AxiosResponse<IBundle[]>> => {
  return Environment.current.dcsApiClient.get<IBundle[]>(`v1/resellers/${resellerId}/bundles`, {
    params: {
      bundleIds: bundleIds.join(','),
    },
  });
};

export const fetchProductsDetail = async ({
  resellerId,
  productIds,
}: {
  resellerId: string;
  productIds: string[];
}): Promise<AxiosResponse<IProduct[]>> => {
  return Environment.current.dcsApiClient.get<IProduct[]>(`v1/resellers/${resellerId}/products`, {
    params: {
      productIds: productIds.join(','),
    },
  });
};

export const fetchAllDCSProducts = async (resellerId?: string): Promise<AxiosResponse<IProduct[]>> =>
  Environment.current.dcsApiClient.get<IProduct[]>(`v1/resellers/${resellerId}/products`);

export const fetchAllProducts = async (): Promise<AxiosResponse<IProduct[]>> => {
  return Environment.current.dcsApiClient.get<IProduct[]>(`v1/products`);
};

export const fetchAllDCSBundles = async (resellerId?: string): Promise<AxiosResponse<IBundle[]>> =>
  Environment.current.dcsApiClient.get<IBundle[]>(`v1/resellers/${resellerId}/bundles`);

export const fetchSoftwarePlanDetail = async ({
  resellerId,
  softwarePlanId,
}: {
  resellerId: string;
  softwarePlanId: string;
}): Promise<AxiosResponse<ISoftwarePlan>> => {
  return Environment.current.dcsApiClient.get<ISoftwarePlan>(
    `v1/resellers/${resellerId}/software-plans/${softwarePlanId}`,
  );
};

export const fetchAllDCSSoftwarePlans = async (
  resellerId?: string,
): Promise<AxiosResponse<ISoftwarePlan[]>> => {
  return Environment.current.dcsApiClient.get<ISoftwarePlan[]>(`v1/resellers/${resellerId}/software-plans`);
};
